import React, { useState, useEffect } from 'react';
import api from '../api';
import { Search, RotateCw, RefreshCcw, Download } from 'lucide-react';
import { format } from 'date-fns';

export const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState(null); // tx id being acted upon

  useEffect(() => { fetchTransactions(); }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/transactions?limit=100');
      setTransactions(response.data.data?.transactions || response.data.data || []);
    } catch (error) {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  async function refund(tx) {
    const amount = window.prompt(
      `Refund how much for ${tx.gatewayTransactionId}?\nLeave blank for full refund (${tx.amount}):`,
      ''
    );
    if (amount === null) return;
    const reason = window.prompt('Reason for refund?', 'requested_by_customer') || 'requested_by_customer';
    setBusy(tx.id);
    try {
      const { data } = await api.post(`/admin/transactions/${tx.id}/refund`, {
        amount: amount ? Number(amount) : undefined,
        reason,
      });
      alert(`Refund ${data.data.refundStatus}. Ref: ${data.data.refundRef}`);
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.error?.message || err.message);
    } finally {
      setBusy(null);
    }
  }

  async function retry(tx) {
    if (!window.confirm(`Retry this failed contribution ($${tx.amount})?`)) return;
    setBusy(tx.id);
    try {
      const { data } = await api.post(`/admin/transactions/${tx.id}/retry`);
      alert(`Retry result: ${data.data.result}`);
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.error?.message || err.message);
    } finally {
      setBusy(null);
    }
  }

  function exportCsv() {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (search) params.set('search', search);
    // Authorization via query token isn't ideal but avoids a blob-download dance.
    // Instead: hit through fetch with the auth header and trigger download.
    api
      .get(`/admin/transactions/export.csv?${params.toString()}`, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => alert(err.response?.data?.error?.message || err.message));
  }

  const filtered = transactions
    .filter(tx => !search || `${tx.user?.firstName} ${tx.user?.lastName} ${tx.id} ${tx.gatewayTransactionId || ''}`.toLowerCase().includes(search.toLowerCase()))
    .filter(tx => !statusFilter || tx.status === statusFilter);

  return (
    <div>
      <div className="card-header" style={{ padding: '0 0 24px 0', border: 'none' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Transactions</h1>
        <button className="btn-secondary" onClick={exportCsv} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="glass">
        <div className="card-header">
          <div className="action-bar" style={{ margin: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-secondary)' }} />
              <input type="text" placeholder="Search by user, tx id, or Stripe PI id…" className="search-input" style={{ paddingLeft: 40, width: 320 }} value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="dropdown" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>
        </div>

        <div className="table-container" style={{ border: 'none', borderRadius: '0 0 16px 16px' }}>
          {loading ? (
            <div className="loader-container"><div className="spinner"></div></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Transaction</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(tx => (
                  <tr key={tx.id}>
                    <td>
                      <div style={{ fontFamily: 'monospace', color: 'var(--accent)', fontSize: 12 }}>{tx.id.substring(0, 10)}…</div>
                      {tx.gatewayTransactionId && (
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
                          {tx.gatewayTransactionId.substring(0, 22)}…
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {tx.user?.firstName} {tx.user?.lastName}
                      {tx.user?.email && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{tx.user.email}</div>}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{tx.type}</td>
                    <td style={{ fontWeight: 600 }}>${Number(tx.amount).toFixed(2)}</td>
                    <td><span className={`badge ${(tx.status || 'pending').toLowerCase()}`}>{tx.status || 'PENDING'}</span></td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                      {tx.createdAt ? format(new Date(tx.createdAt), 'MMM dd, HH:mm') : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['COMPLETED', 'PROCESSING'].includes(tx.status) && tx.gatewayTransactionId && (
                          <button className="btn-danger" disabled={busy === tx.id} onClick={() => refund(tx)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <RefreshCcw size={12} /> Refund
                          </button>
                        )}
                        {tx.status === 'FAILED' && tx.type === 'CONTRIBUTION' && (
                          <button className="btn-secondary" disabled={busy === tx.id} onClick={() => retry(tx)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <RotateCw size={12} /> Retry
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>No transactions found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
